import { combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, groupBy, map, mergeMap, startWith, throttleTime } from 'rxjs/operators';
import { Set } from 'immutable';
import { requestActions } from './apiHelpers';
import { ID, ReduxAction } from '@repo/utils/types';
import { apiObservable } from '@repo/utils/helpers/api';
import Contact from '@repo/utils/models/Contact';
import { alertUser } from './alerts';
import { createSearchActions } from './search';

const archivedContactActions = requestActions('ARCHIVED_CONTACT', ['fetchAll', 'fetch']);
const archivedContactBatchActions = requestActions('ARCHIVED_CONTACT_BATCH', ['delete', 'restore']);

export const fetchArchive = archivedContactActions['fetchAll'];
export const fetchMoreArchive = () => ({ type: 'FETCH_MORE_ARCHIVED_CONTACTS' });
export const fetchArchivedContact = (id: ID) => archivedContactActions['fetch']({ id });

export const restoreArchivedContact = (id: ID) => ({
  type: 'RESTORE_ARCHIVED_CONTACT_BATCH',
  payload: { contactIDs: Set([id]) },
});
export const restoreArchivedContactBatch = (contactIDs: Set<ID>) => ({
  type: 'RESTORE_ARCHIVED_CONTACT_BATCH',
  payload: { contactIDs },
});
export const setSelectedArchivedContact = (contact: Contact) => ({
  type: 'SET_SELECTED_ARCHIVED_CONTACT',
  payload: { contact },
});
export const setSelectedArchivedContactBatch = (contactBatch: Set<ID>) => ({
  type: 'SET_SELECTED_ARCHIVED_CONTACT_BATCH',
  payload: { contactBatch },
});

// Epic helpers
const fetchArchivedContactObservable = (id: ID) =>
  apiObservable('GET', `/contacts/archived/${id}/`, {}, { apiVersion: 'v2' });

const restoreArchivedObservable = (ids: ID[]) =>
  apiObservable('PATCH', '/contacts/archived/restore/', { ids }, { apiVersion: 'v2' });

const fetchArchivedContactEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('FETCH_ARCHIVED_CONTACT'),
    groupBy(({ payload }) => payload.id),
    mergeMap((fetchesByID) =>
      fetchesByID.pipe(
        // For a given archived contact, only re-fetch at most once every 1 minute
        // This data will be overwritten if we refetch the first page of the Archive, but this doesn't seem critical
        throttleTime(1000 * 60 * 1),
        mergeMap(({ payload }) =>
          fetchArchivedContactObservable(payload.id).pipe(
            map((response) =>
              archivedContactActions['fetchSucceeded']({ ...response, contactID: payload.id }),
            ),
            startWith(archivedContactActions['fetchRequested'](payload)),
            catchError((err) => of(archivedContactActions['fetchFailed']({ ...err }))),
          ),
        ),
      ),
    ),
  );

const restoreArchivedContactsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('RESTORE_ARCHIVED_CONTACT_BATCH'),
    mergeMap(({ payload }) => {
      const contactIDs = payload.contactIDs.toArray();
      return restoreArchivedObservable(contactIDs).pipe(
        map(() => archivedContactBatchActions['restoreSucceeded'](payload)),
        startWith(archivedContactBatchActions['restoreRequested'](payload)),
        catchError((err) =>
          of(archivedContactBatchActions['restoreFailed']({ ...err, ...payload })),
        ),
      );
    }),
  );

const archiveActionAlertsEpic = (action$: Observable<ReduxAction>) =>
  action$.pipe(
    ofType('RESTORE_ARCHIVED_CONTACT_BATCH_SUCCEEDED', 'RESTORE_ARCHIVED_CONTACT_BATCH_FAILED'),
    map(({ payload, type }) => {
      let action = 'restore';
      const alertType = type.includes('SUCCEEDED') ? 'success' : 'error';
      const count = payload.contactIDs.size;
      const unit = count > 1 ? 'People' : 'Person';

      if (alertType == 'success') {
        action = `${action}d`;
      } else {
        action = `Failed to ${action}`;
      }

      return alertUser(`${action} ${count} ${unit}`, alertType);
    }),
  );

const [
  _archiveSearchActionTypes,
  archiveSearchActions,
  fetchArchiveSearchResultsEpic,
  _sortAndFetchArchiveSearchResultsEpic,
  fetchMoreArchiveSearchResultsEpic,
] = createSearchActions('archiveSearch');

export const setArchiveSearchTerm = (term) => archiveSearchActions.setTerm(term || 'all', 100, []);
export const fetchMoreArchiveSearchResults = archiveSearchActions.fetchMore;

export const archiveEpic = combineEpics(
  fetchArchiveSearchResultsEpic,
  fetchMoreArchiveSearchResultsEpic,
  fetchArchivedContactEpic,
  restoreArchivedContactsEpic,
  archiveActionAlertsEpic,
);
